import type { DayRollup } from '@baby-tracker/contracts';
import { lazy, Suspense, type ReactElement } from 'react';

import { formatDuration } from '../../../lib/activity-format.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import styles from './tracking.module.css';

const SleepWeekChart = lazy(() =>
    import('../../charts/SleepWeekChart.js').then((module) => ({ default: module.SleepWeekChart })),
);

export function WeekChart({
    days,
    childId,
    error,
}: {
    days: DayRollup[] | undefined;
    childId: string | undefined;
    error: Error | null;
}): ReactElement {
    const points =
        days?.map((day) => {
            const child = day.children.find((row) => row.childId === childId);
            return { date: day.date, sleepSeconds: child?.sleepSeconds ?? 0 };
        }) ?? [];
    const totalSleep = points.reduce((sum, point) => sum + point.sleepSeconds, 0);
    const summary =
        points.length === 0
            ? 'No days to chart yet.'
            : totalSleep === 0
              ? 'No sleep in this week.'
              : `${formatDuration(totalSleep)} of sleep across these days.`;

    return (
        <Card title="Sleep this week" headingLevel={2}>
            {error ? <FormError message={errorText(error)} /> : null}
            <p className={styles.summary}>{summary}</p>
            {points.length > 0 ? (
                <div
                    className={styles.chartFrame}
                    role="img"
                    aria-label={`${summary} Bars show sleep for each weekday.`}
                >
                    <Suspense fallback={<p className={styles.meta}>Loading chart</p>}>
                        <SleepWeekChart days={days ?? []} childId={childId} />
                    </Suspense>
                </div>
            ) : null}
        </Card>
    );
}
